import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage } from "firebase/storage";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from "firebase/app-check";
import logger from "./utils/logger";

// Firebase looks for a global FIREBASE_APPCHECK_DEBUG_TOKEN on self in dev.
declare global {

  interface Window { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);

// App Check — prevents unauthorized API access from scripts/bots.
// Uses debug token in development, reCAPTCHA Enterprise in production.
if (process.env.NODE_ENV === "development") {
   
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

export let appCheck: AppCheck | null = null;

if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(
      process.env.REACT_APP_RECAPTCHA_SITE_KEY
    ),
    isTokenAutoRefreshEnabled: true,
  });
} else if (process.env.NODE_ENV === "production") {
  // Fail the build/boot rather than silently shipping without App Check.
  throw new Error(
    "SECURITY: REACT_APP_RECAPTCHA_SITE_KEY must be set for production builds. " +
    "Set the key and enforce App Check in the Firebase console before deploying."
  );
} else {
  logger.warn("App Check disabled: REACT_APP_RECAPTCHA_SITE_KEY is not set.");
}

export const auth = getAuth(app);
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});
export const storage = getStorage(app);

// Connect to Firebase emulators when REACT_APP_USE_EMULATOR is set.
// (Static imports, not require(): Vite serves ESM, where require is undefined.)
if (process.env.REACT_APP_USE_EMULATOR === "true") {
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  // Callables (platform panel, test environment, job actions) go to the
  // functions emulator too; without this they would hit the real project.
  const [fnHost, fnPort] = (process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || "localhost:5001").split(":");
  connectFunctionsEmulator(getFunctions(app), fnHost, Number(fnPort) || 5001);
}
